import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Grid, Divider, Box, Drawer, Button, CircularProgress } from "@material-ui/core";
import Pagination from "@material-ui/lab/Pagination";
import clsx from "clsx";
import SearchService from "../../services/SearchService";
import apiClient from "../../auth/apiClient";
import useCurrentFacility from "../../hooks/useCurrentFacility";
import { useSelector } from "react-redux";
import { useFeatureFlag } from "../../hooks/useFeatureFlags";
import { useLocation, useHistory } from "react-router-dom";
import OneParkAccountSearchButton from "./OneParkAccountSearchButton";
import OneParkAccountTable from "./OneParkAccountTable";
import AccessHolderForm from "../Forms/AccessHolder";
import AccessGroupProvider from "../../providers/AccessGroupsProvider";
import { useStyles } from "./styles";
import useCurrentFacilityTimezone from "../../hooks/useCurrentFacilityTimezone";

const ITEM_LIMIT = 10;
const ONEPARK_ACCOUNT_SEARCH_KEY = "oneparkAccountSearch";

const searchService = new SearchService(apiClient);

const NUMERIC_FIELDS = ["accountBalance", "points"];

const descendingComparator = (a, b, orderBy) => {
  if (orderBy === "lastUse") {
    const aTime = a.lastUseRaw ? new Date(a.lastUseRaw).getTime() : 0;
    const bTime = b.lastUseRaw ? new Date(b.lastUseRaw).getTime() : 0;
    if (aTime === 0 && bTime === 0) return 0;
    if (bTime === 0) return -1;
    if (aTime === 0) return 1;
    return bTime - aTime;
  }
  if (NUMERIC_FIELDS.includes(orderBy)) {
    const aVal = a[orderBy] === "" || a[orderBy] == null ? -Infinity : Number(a[orderBy]);
    const bVal = b[orderBy] === "" || b[orderBy] == null ? -Infinity : Number(b[orderBy]);
    return bVal - aVal;
  }
  if (!b[orderBy]) return -1;
  if (!a[orderBy]) return 1;
  return b[orderBy]
    .toString()
    .toLowerCase()
    .localeCompare(a[orderBy].toString().toLowerCase());
};

const getComparator = (order, orderBy) =>
  order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);

const OneParkAccountContainer = () => {
  const classes = useStyles();
  const { facilityID } = useCurrentFacility();
  const { convertUtcDateToFormat } = useCurrentFacilityTimezone();
  
  const scopeAwareFacilityID = useSelector(
    (state) => state.entityScope?.facilityGroupId || facilityID
  );

  const rewardsEnabled = useFeatureFlag("ONEpark.Rewards");

  const location = useLocation();
  const history = useHistory();
  const isReturningFromCardFlow = !!location.state?.openDrawerAccessHolderID;

  const [activeSearch, setActiveSearch] = useState(null);
  const [loading, setLoading] = useState(isReturningFromCardFlow);
  const [rows, setRows] = useState([]);
  const [totalCount, setTotalCount] = useState(0);

  const [orderBy, setOrderBy] = useState("email");
  const [order, setOrder] = useState("asc");
  const [page, setPage] = useState(1);

  const [activeChip, setActiveChip] = useState(null);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);

  useEffect(() => {
    const openDrawerID = location.state?.openDrawerAccessHolderID;
    const cardMessage = location.state?.cardOnFileMessage;
    if (openDrawerID) {
      
      const savedSearch = localStorage.getItem(ONEPARK_ACCOUNT_SEARCH_KEY);
      if (savedSearch) {
        try {
          const parsed = JSON.parse(savedSearch);
          setActiveSearch(parsed.activeSearch);
          setActiveChip(parsed.activeChip);
          setPage(parsed.page || 1);
        } catch {
          localStorage.removeItem(ONEPARK_ACCOUNT_SEARCH_KEY);
        }
      }
      setSelectedRow({ id: openDrawerID, cardOnFileMessage: cardMessage });
      setDrawerOpen(true);
    }  
    }, []);

  const handleRowClick = (row) => {
    setSelectedRow({ ...row });
    setDrawerOpen(true);
    localStorage.setItem(ONEPARK_ACCOUNT_SEARCH_KEY, JSON.stringify({
      activeSearch,
      activeChip,
      page
    }));
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    localStorage.removeItem(ONEPARK_ACCOUNT_SEARCH_KEY);
    if (location.state?.openDrawerAccessHolderID) {
      history.replace(location.pathname, {});
    }
  };

  const fetchAccountResults = useCallback(async () => {
    if (!activeSearch) return;

    setLoading(true);
    try {
      const offset = (page - 1) * ITEM_LIMIT;
      const response = await searchService.getAccounts(
        scopeAwareFacilityID,
        activeSearch.searchTerm,
        activeSearch.searchCategory,
        activeSearch.accountFilter,
        null,
        ITEM_LIMIT,
        offset
      );

      const formatted =
        response?.data?.collection?.map((r) => ({
          id: r.accessHolderID,
          email:
            r.contactInfo?.emails?.length > 0
              ? r.contactInfo.emails[0].emailAddress
              : "",
          points: r.points ?? "",
          signupDate: r.signUpDate ? convertUtcDateToFormat(r.signUpDate) : "--",
          lastUseRaw: r.lastUse,
          lastUse: r.lastUse ? convertUtcDateToFormat(r.lastUse) : "--",
          accountBalance: r.accountBalance ?? "",
          status: r.status ?? "",
        })) ?? [];

      setRows(formatted);
      setTotalCount(response?.data?.totalCount ?? 0);
    } catch {
      setRows([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  }, [activeSearch, scopeAwareFacilityID, page]);

  useEffect(() => {
    fetchAccountResults();
  }, [fetchAccountResults]);

  const handleSearch = useCallback((search) => {
    setLoading(true);
    setActiveSearch({ ...search, accountFilter: activeChip || "None" });
    setPage(1);
  }, [activeChip]);



  const sortedRows = useMemo(
    () => [...rows].sort(getComparator(order, orderBy)),
    [rows, order, orderBy]
  );

  return (
    <Box className="onepark-account-container">
      <Grid container>
        <Grid item xs={12} md={9}>
          <OneParkAccountSearchButton
            loading={loading}
            onSearch={handleSearch}
            activeChip={activeChip}
          />

          <Divider className={classes.sectionDivider} />

          <Box className={classes.chipSection}>
            <span className={classes.chipLabel}>
              Select Accounts With:
            </span>
            <span
              className={clsx(classes.accountChip, { [classes.accountChipActive]: activeChip === "noCredentials" })}
              onClick={() => { setActiveChip((prev) => prev === "noCredentials" ? null : "noCredentials"); setPage(1); }}
            >
              No Credentials
            </span>
            <span
              className={clsx(classes.accountChip, { [classes.accountChipActive]: activeChip === "ExpiredCreditCard" })}
              onClick={() => { setActiveChip((prev) => prev === "ExpiredCreditCard" ? null : "ExpiredCreditCard"); setPage(1); }}
            >
              Expired Credit Cards
            </span>
          </Box>

          <Divider className={classes.chipDivider} />
        </Grid>
      </Grid>

      {activeSearch && totalCount > ITEM_LIMIT && (
        <div className={classes.paginationWrapper}>
          <Pagination
            page={page}
            count={Math.ceil(totalCount / ITEM_LIMIT)}
            onChange={(e, value) => setPage(value)}
            size="small"
            shape="rounded"
            color="primary"
          />
        </div>
      )}

      {loading && !activeSearch && (
        <Box display="flex" justifyContent="center" alignItems="center" py={4}>
          <CircularProgress />
        </Box>
      )}

      {activeSearch && (
        <div className={classes.contentWrapper}>
          {loading && sortedRows.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" py={4}>
              <CircularProgress />
            </Box>
          ) : sortedRows.length > 0 ? (
            <OneParkAccountTable
              rows={sortedRows}
              order={order}
              orderBy={orderBy}
              rewardsEnabled={rewardsEnabled}
              onSort={(column) => {
                const isAsc = orderBy === column && order === "asc";
                setOrder(isAsc ? "desc" : "asc");
                setOrderBy(column);
              }}
              onRowClick={handleRowClick}
            />
          ) : (
            <div className={clsx(classes.noResults, "onepark-account-no-results")}>
              <span className={clsx("no-results-text", "onepark-account-no-results-text")}>No results found</span>
            </div>
          )}
        </div>
      )}

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleDrawerClose}
        classes={{ paper: classes.drawerPaper }}
      >
        <Button onClick={handleDrawerClose}>Close</Button>
        <div style={{ padding: 20 }}>
          {selectedRow && (
            <AccessGroupProvider>
              <AccessHolderForm
                accessHolderID={selectedRow.id}
                facilityID={facilityID}
                onDelete={handleDrawerClose}
                returnContext="onepark"
              />
            </AccessGroupProvider>
          )}
        </div>
      </Drawer>
    </Box>
  );
};

export default OneParkAccountContainer;